import { Component, OnInit, OnDestroy, Input, AfterViewInit, EventEmitter, Output, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ManagedContentService } from '../shared/service/managed-content.service';
import { UserSessionService } from '../shared/service/user-session.service';
import { UntypedFormGroup, UntypedFormControl, Validators, AbstractControl, ReactiveFormsModule, ValidationErrors } from '@angular/forms';
import { CmElement, ContentType, CmTooltip, CmSection} from '../shared/model/cm/managed-content.model';
import { EEOI_APP_ROUTES, GlobalConstants } from '../shared/global-constants';
import { EmployeeMhs } from '../shared/model/session/employee-mhs';
import { FormUtils } from '../shared/helper/form-utils';
import { MhsService } from '../shared/service/mhs.service';
import { AuthGuardService } from '../shared/service/auth-guard.service';
import { AppInsightsApiService } from '../shared/service/app-insights-api.service';
import { of, Subscription } from 'rxjs';
import { PagetitleService } from '../shared/service/pagetitle.service';
import { ModalService } from '../shared/service/modal.service';
import { GoogleAnalyticsService } from '../shared/service/google-analytics.service';
import { FormControlUtilitiesService } from "../shared/service/form-control-utilities.service";
import { CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';

import { ContinueCancelComponent } from '../continue-cancel/continue-cancel.component';
import { ApplicantMhs } from '../shared/model/session/applicant-mhs';
import { PartialSaveResp } from '../shared/model/partial-save-resp';
import { ApplicationService } from '../shared/service/application.service';
import { AppInsightsLoggerService } from '../shared/service/app-insights-logger.service';
import { PartialSaveData } from '../shared/model/partial-save-data';
import { environment } from '@environment';

@Component({
  selector: 'app-employee-info',
  standalone: true,
  imports: [ReactiveFormsModule, ContinueCancelComponent],
  templateUrl: './employee-info.component.html',
  styleUrls: ['./employee-info.component.scss'],
  providers: [ContinueCancelComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class EmployeeInfoComponent implements OnInit, AfterViewInit, OnDestroy {
  @Output() val = new EventEmitter<boolean>();
  @Output() cancelEmitter = new EventEmitter<void>();

  public companyId: string = '';
  public dateOfBirthHint: string = '';
  public environmentName: string = '';

  public timeInMilliseconds: string = '';
  public ssnInvalidErrorElement!: CmElement;
  public ssnMatchErrorElement!: CmElement;
  public employeeBirthDateDayName: string = "birthDateDayInput";
  public employeeBirthDateMonthName: string = "birthDateMonthInput";
  public employeeBirthDateYearName: string = "birthDateYearInput";
  public employeeFirstNameName: string = "employeeFirstName";
  public employeeLastNameName: string = "employeeLastName";
  public employeeHireDateDayName: string = "employeeHireDateDay";
  public employeeHireDateMonthName: string = "employeeHireDateMonth";
  public employeeHireDateYearName: string = "employeeHireDateYear";
  public positionName: string = "position";
  public employeeEarningsName: string = "earnings";
  public employeeSsnName: string = "employeeSsn";
  public formSubmitted: boolean = false;
  public isEmployeeViewMode: boolean = false; // Track if in readonly/view mode
  public isAutoSubmitting: boolean = false; // Track if auto-submitting in continue mode

  /**
  * True when the user reached /employee-information via the review
   * Overview "View" button (URL ?mode=edit or history.state.editEmployee).
   * In that mode the page shows Back / Save buttons instead of Continue / Cancel
   * and the Save button persists the changes then returns to /review.
   */
  public isEditFromReview: boolean = false;

  /**
   * True when employee information is opened from auth-elec-sign as
   * a dependent (spouse/child) review. In this mode the user can only
   * view employee data and go back; editing and saving are disabled.
   */
  public isViewOnlyFromReview: boolean = false;

  /**
   * True while an edit-mode save round-trip is in flight. Used to disable
   * the Save / Back buttons so a double-click cannot trigger duplicate
   * backend writes or a navigation race.
   */
  public isSavingEdit: boolean = false;
  /** Set to true when the user clicks Back without saving so ngOnDestroy
   *  does not overwrite the session cache with unsaved (possibly empty) form data. */
  private discardFormDataOnDestroy: boolean = false;

  public actualSsnValue: string = '';
  public formNumber: string = this.userSessionService.data.formNumber;
  public isGenericDynamicForm: boolean = this.userSessionService.data.isGenericDynamicForm;

  public continueButtonProps = {
    keyId: 'continue-button',
    size: 'large',
    text: 'Continue',
    type: 'submit',
    variant: 'primary'
  }

  public maxLengthName = GlobalConstants.MAX_LENGTH_NAME;
  public maxlengthEmployer = GlobalConstants.MAX_LENGTH_EMPLOYER;
  public maxLengthOccupation = GlobalConstants.MAX_LENGTH_OCCUPATION;
  public maxLengthSalary = GlobalConstants.MAX_LENGTH_SALARY;
  public readonly NAME_PATTERN = GlobalConstants.NAME_PATTERN;

  public readonly today = new Date();

  //100 years past from current date
  public readonly birthDayStartDate = new Date((this.today.getFullYear() + GlobalConstants.MONTH_PICKER_MIN_DATE_YEAR_OFFSET), GlobalConstants.MONTH_PICKER_MIN_DATE_MONTH_OFFSET, GlobalConstants.MONTH_PICKER_MIN_DATE_DAY_OFFSET);
  public readonly birthDayDatePickerStartAt = new Date((this.today.getFullYear() - GlobalConstants.DATE_PICKER_BIRTH_DATE_RANGE_START_YEAR_OFFSET), GlobalConstants.MONTH_PICKER_MIN_DATE_MONTH_OFFSET, GlobalConstants.MONTH_PICKER_MIN_DATE_DAY_OFFSET);

  public tooltipEmployerKeyId: string = '';
  public tooltipEmployerHeading: string = '';
  public tooltipEmployerDescription: string = '';

  public tooltipEarningsKeyId: string = '';
  public tooltipEarningsHeading: string = '';
  public tooltipEarningsDescription: string = '';
  public hasSsnMatch = false;

  private submitted: boolean = false;
  private subscriptions: Subscription[] = [];
  private readonly namePatternRegex = new RegExp(this.NAME_PATTERN);

  private readonly trimmedNamePatternValidator = (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (value === null || value === undefined || value === '') {
      return null;
    }

    return this.namePatternRegex.test(value.toString().trim()) ? null : { pattern: true };
  };

  @Input() parentForm!: UntypedFormGroup;

  employeeInfoForm!: UntypedFormGroup;

  //Display items
  firstNameElement!: CmElement;
  lastNameElement!: CmElement;
  dateOfBirthElement!: CmElement;
  employerElement!: CmElement;
  dateOfHireElement!: CmElement;
  occupationElement!: CmElement;
  salaryElement!: CmElement;
  ssnElement!: CmElement;
  jobTitleElement!: CmElement;
  jobTitleErrorElement!: CmElement;
  occupationErrorElement!: CmElement;

  employerTooltip!: CmTooltip;
  earningsTooltip!: CmTooltip;

  formSection!: CmSection;
  pageTitle: string = '';
  pageInstruction: string = '';
  pageInstructions: string[] = [];
  fistNameValid: boolean = true;

  // GA4 Custom events
  public submitEmployeeInfo = {
    action: "button_click",
    category: "employee-info",
    name: "submit",
  }

  public cancelEmployeeInfo = {
    action: "button_click",
    category: "employee-info",
    name: "cancel",
  }

  constructor(private router: Router,
              private activatedRoute: ActivatedRoute,
              private cmService: ManagedContentService,
              private userSessionService: UserSessionService,
              private googleAnalyticsService: GoogleAnalyticsService,
              private mhsService: MhsService,
              private authGuardService: AuthGuardService,
              private appInsightsApiService: AppInsightsApiService,
              private pagetitleService: PagetitleService,
              private modalService: ModalService,
              public formControlUtilitiesService: FormControlUtilitiesService,
              private cdr: ChangeDetectorRef,
              private applicationService: ApplicationService,
              private appInsightsLoggerService: AppInsightsLoggerService
              ) {

              }

  get employeeFirstName() {
    return this.employeeInfoForm.get("employeeFirstName");
  }

  get employeeLastName() {
    return this.employeeInfoForm.get("employeeLastName");
  }

  get employer() {
    return this.employeeInfoForm.get("employer");
  }

  get position() {
    return this.employeeInfoForm.get("position");
  }

  get earnings() {
    return this.employeeInfoForm.get("earnings");
  }

  get employeeSsn() {
    return this.employeeInfoForm.get("employeeSsn");
  }

  trimSSN(){
    this.employeeInfoForm.get('employeeSsn')?.setValue(this.actualSsnValue);
  }

  handleChangeEvent(event: Event): void {
    const customEvent = event as CustomEvent<InputEvent>;
    this.actualSsnValue = customEvent.detail.toString();
    this.employeeInfoForm.get('actualSsn')?.setValue(this.actualSsnValue);
  }

  public ssnControl= new UntypedFormControl(null, [Validators.required, Validators.pattern(GlobalConstants.SSN_VALIDATOR_PATTERN)]);

  ngOnInit() {
    if(this.userSessionService.data.sessionEnded){
      this.router.navigate(['/instructions']);
    }

    // Reset form submission state when navigating back to this page
    this.formSubmitted = false;
    this.submitted = false;

    // Detect edit-mode entry from the review Overview "View" button.
    // Mirrors the same detection used by coverage.component.ts so the page
    // can show Back / Save buttons and persist changes via a partial update
    // instead of the standard Continue / Cancel flow.
    const isEditFromState = history.state && history.state.editEmployee === true;
    const isEditFromQuery = this.activatedRoute.snapshot.queryParamMap.get('mode') === 'edit';
    this.isEditFromReview = isEditFromState || isEditFromQuery;
    
    const isViewFromState = history.state && history.state.viewEmployee === true;
    const isViewFromQuery = this.activatedRoute.snapshot.queryParamMap.get('mode') === 'view';
    const isFromMhsProgressBar = history.state && history.state.fromMhsProgressBar === true;
    const applicantType = this.userSessionService.data?.applicantType?.toLowerCase();
    const isDependentApplicant = applicantType === 'spouse' || applicantType === 'child';

    // Dependent employee review can come from:
    // 1) auth-elec-sign Overview View button -> auth-only Back/Save mode
    // 2) MHS progress bar -> readonly fields with normal wizard Cancel/Continue buttons
    this.isViewOnlyFromReview = (isViewFromState || isViewFromQuery) && isDependentApplicant && !isFromMhsProgressBar;

    if ((isViewFromState || isViewFromQuery) && isDependentApplicant) {
      this.isEmployeeViewMode = true;
    }

    if (this.isViewOnlyFromReview) {
      this.isEditFromReview = true;
    }
    
    // Check if continuing from confirmation OR if we have preserved employee data (navigating back)
    const isContinueMode = (this.userSessionService.data.isContinuingFromConfirmation || 
                           this.userSessionService.data.preservedEmployeeData?.employeeMhs) && 
                          this.userSessionService.data.preservedEmployeeData?.employeeMhs;

    this.environmentName = environment.name;

    this.companyId = this.userSessionService.data.companyId;

    let today = new Date();
    this.timeInMilliseconds = today.getTime().toString();

    // A policy in sessionStorage indicates that a custom url was used
    let policyId = sessionStorage.getItem(GlobalConstants.CUSTOM_URL_PARAM_NAME);

    this.firstNameElement = this.cmService.getContentById(ContentType.ELEMENT, 'first-name');
    this.lastNameElement = this.cmService.getContentById(ContentType.ELEMENT, 'last-name');
    this.dateOfBirthElement = this.cmService.getContentById(ContentType.ELEMENT, 'date-of-birth');

    this.formSection = this.cmService.getContentById(ContentType.SECTION, "employee-section");

    // Set content
    this.ssnInvalidErrorElement = this.cmService.getContentById(ContentType.ELEMENT, 'ssnInvalidError');
    this.ssnMatchErrorElement = this.cmService.getContentById(ContentType.ELEMENT, 'ssnMatchEmployeeError');

    this.pageTitle = this.formSection.title ?? '';
    this.pageInstruction = this.formSection.body ?? '';
    this.pageInstruction = this.pageInstruction.replace('<p>',"");
    this.pageInstruction = this.pageInstruction.replace('</p>',"");
    this.pageInstructions = this.pageInstruction.split('.');

    if(policyId != null){
      this.employerElement = this.cmService.getContentById(ContentType.ELEMENT, 'employer-custom-form');
    } else {
      this.employerElement = this.cmService.getContentById(ContentType.ELEMENT, 'employer-generic-form');
    }

    this.dateOfHireElement = this.cmService.getContentById(ContentType.ELEMENT, 'date-of-hire');
    this.dateOfBirthHint = this.dateOfHireElement?.placeholder ?? '';
    if(this.isGenericDynamicForm) {
      this.jobTitleElement = this.cmService.getContentById(ContentType.ELEMENT, 'jobTitle');
      this.jobTitleErrorElement = this.cmService.getContentById(ContentType.ELEMENT, 'jobTitleError');
    } else {
      this.occupationElement = this.cmService.getContentById(ContentType.ELEMENT, 'occupation');
      this.occupationErrorElement = this.cmService.getContentById(ContentType.ELEMENT, 'occupationError');
    }
    this.salaryElement = this.cmService.getContentById(ContentType.ELEMENT, 'annual-base-salary');
    this.ssnElement = this.cmService.getContentById(ContentType.ELEMENT, 'ssn');

    this.employerTooltip = this.cmService.getContentById(ContentType.TOOLTIP, "policyholder-generic-form");
    if(this.employerTooltip){
      this.tooltipEmployerKeyId = 'helpEmployer';
      this.tooltipEmployerHeading = this.employerTooltip.title ?? '';
      this.tooltipEmployerDescription = this.employerTooltip.plainText ?? '';
    }

    this.earningsTooltip = this.cmService.getContentById(ContentType.TOOLTIP, "annual-salary");
    if(this.earningsTooltip){
      this.tooltipEarningsKeyId = 'helpEarnings';
      this.tooltipEarningsHeading = this.earningsTooltip.title ?? '';
      this.tooltipEarningsDescription = this.earningsTooltip.plainText ?? '';
    }

    // Check if form already exists in parent (user navigated back to this page)
    // Check if we have saved form data in session
    const savedEmployeeData = (this.userSessionService.data as any)['employeeFormData'];
    
    if (this.parentForm && this.parentForm.contains('employee')) {
      // Use existing form to preserve data
      this.employeeInfoForm = this.parentForm.get('employee') as UntypedFormGroup;
    } else {
      // Create new form for first visit or after navigation
      this.employeeInfoForm = new UntypedFormGroup({
        employeeFirstName: new UntypedFormControl(null, [
          Validators.required,
          Validators.maxLength(this.maxLengthName),
          this.trimmedNamePatternValidator
        ]),
        employeeLastName: new UntypedFormControl(null, [
          Validators.required,
          Validators.maxLength(this.maxLengthName),
          this.trimmedNamePatternValidator
        ]),
        employeeBirthDateMonth: new UntypedFormControl(null, []),
        employeeBirthDateDay: new UntypedFormControl(null, []),
        employeeBirthDateYear: new UntypedFormControl(null, []),
        employer: new UntypedFormControl(this.userSessionService.data.grpName),
        employeeHireDateMonth: new UntypedFormControl(null, []),
        employeeHireDateDay: new UntypedFormControl(null, []),
        employeeHireDateYear: new UntypedFormControl(null, []),
        position: new UntypedFormControl(null, [
          Validators.required,
          Validators.maxLength(this.maxLengthOccupation)
        ]),
        earnings: new UntypedFormControl(null, [Validators.required, Validators.min(1),
          Validators.maxLength(this.maxLengthSalary)]),
        employeeSsn: this.ssnControl,
        actualSsn: new UntypedFormControl(null),
      });
    }

    if(this.userSessionService.data.applicantType){
      if (this.userSessionService.data.applicantType.toLowerCase() != "employee") {
        this.employeeInfoForm.get("employeeSsn")?.addValidators(this.ssnMatchesApplicantValidator());
      } 
    }

    // Add form to parent if it was newly created
    const wasNewlyCreated = this.parentForm && !this.parentForm.contains('employee');
    if (wasNewlyCreated) {
      this.parentForm.addControl('employee', this.employeeInfoForm);
    } else {
    }

    // Restore saved data AFTER adding to parent (only if we just created a new form)
    if (savedEmployeeData && wasNewlyCreated) {
      // For prism-ui components, we need to set values immediately before the view renders
      // Set values synchronously first
      Object.keys(savedEmployeeData).forEach(key => {
        const control = this.employeeInfoForm.get(key);
        if (control && savedEmployeeData[key] !== null && savedEmployeeData[key] !== undefined) {
          control.setValue(savedEmployeeData[key], { emitEvent: false });
          // Mark as untouched and pristine immediately to prevent validation errors
          control.markAsUntouched();
          control.markAsPristine();
        }
      });
      
      // Also set actualSsnValue if employeeSsn has a value
      if (savedEmployeeData['employeeSsn']) {
        this.actualSsnValue = savedEmployeeData['employeeSsn'];
      }
      
      // Then schedule a second update with events to trigger web component updates
      setTimeout(() => {
        Object.keys(savedEmployeeData).forEach(key => {
          const control = this.employeeInfoForm.get(key);
          if (control && savedEmployeeData[key] !== null && savedEmployeeData[key] !== undefined) {
            control.setValue(savedEmployeeData[key], { emitEvent: true });
            // Mark as untouched and pristine to prevent validation errors from showing
            control.markAsUntouched();
            control.markAsPristine();
            control.updateValueAndValidity();
          }
        });
        this.cdr.detectChanges();
        // Force web components to update by dispatching input events
        setTimeout(() => {
          this.forceWebComponentUpdate();
        }, 50);
      }, 100);
    }

    this.today.setDate(this.today.getDate());

    if(this.mhsService){
      const sub1 = this.mhsService.submitComponent.subscribe(msg => {
        if(msg){
          this.onSubmit();
        }
      });
      this.subscriptions.push(sub1);
    }

    if(this.mhsService){
      const sub2 = this.mhsService.submitEmployeeInfo.subscribe(msg => {
        if(msg){
          this.onSubmit();
        }
      });
      this.subscriptions.push(sub2);
    }

    this.disableAutoFill();

    // Only set employer if we don't have saved data (first visit)
    if (!savedEmployeeData) {
      this.employer?.setValue(this.userSessionService.data.grpName);
    }
    
    // Handle continue mode: restore preserved employee data, populate form, disable, and auto-submit
    if (isContinueMode) {
      const preservedEmployeeMhs = this.userSessionService.data.preservedEmployeeData.employeeMhs;
      
      if (preservedEmployeeMhs) {
        // Set view mode and auto-submitting flags
        this.isEmployeeViewMode = true;
        
        // If continuing from confirmation (not navigating back), show loading state
        if (this.userSessionService.data.isContinuingFromConfirmation) {
          this.isAutoSubmitting = true;
        }
        
        // Parse birthDate (format: YYYY-MM-DD)
        const birthDateParts = preservedEmployeeMhs.birthDate?.split('-');
        const hireDateParts = preservedEmployeeMhs.hireDate?.split('-');
        
        // Wait for form to be fully initialized
        setTimeout(() => {
          // Populate form fields with preserved data
          this.employeeInfoForm.patchValue({
            employeeFirstName: preservedEmployeeMhs.firstName || '',
            employeeLastName: preservedEmployeeMhs.lastName || '',
            employer: preservedEmployeeMhs.employer || '',
            employeeSsn: preservedEmployeeMhs.ssn || '',
            employeeBirthDateYear: birthDateParts?.[0] || '',
            employeeBirthDateMonth: birthDateParts?.[1] || '',
            employeeBirthDateDay: birthDateParts?.[2] || '',
            employeeHireDateYear: hireDateParts?.[0] || '',
            employeeHireDateMonth: hireDateParts?.[1] || '',
            employeeHireDateDay: hireDateParts?.[2] || '',
            position: preservedEmployeeMhs.position || '',
            earnings: preservedEmployeeMhs.earnings || ''
          }, { emitEvent: true });
          
          // Set actualSsnValue
          this.actualSsnValue = preservedEmployeeMhs.ssn;
          
          // Restore to session
          this.userSessionService.data.employee = this.userSessionService.data.preservedEmployeeData.employee;
          
          // Restore mhsApplication.employee so the data persists
          if (this.userSessionService.data.mhsApplication) {
            this.userSessionService.data.mhsApplication.employee = { ...preservedEmployeeMhs };
          }
          
          // Mark form as valid and submitted to skip validation
          this.employeeInfoForm.markAllAsTouched();
          this.employeeInfoForm.updateValueAndValidity();
          
          this.cdr.detectChanges();
          
          // Only auto-submit if we're coming from confirmation (not navigating back)
          if (this.userSessionService.data.isContinuingFromConfirmation) {
            // Auto-submit immediately since we're showing loading state
            setTimeout(() => {
              this.submitted = true;
              this.val.emit(true);
            }, 500);
          }
        }, 200);
      }
    }

  }

  ngAfterViewInit() {
    this.pagetitleService.setPageTitle('Employee Information');
    this.appInsightsApiService.endNavigationEvent(this.router.url);
  }

  ngOnDestroy() {
    // Unsubscribe from all subscriptions to prevent memory leaks and duplicate calls
    this.subscriptions.forEach(sub => sub.unsubscribe());
    
    // Save form data to session before component is destroyed.
    // Skip if the user clicked Back without saving (discardFormDataOnDestroy) to
    // prevent overwriting the session cache with unsaved/empty form data.
    if (this.employeeInfoForm && !this.discardFormDataOnDestroy) {
      const formValue = this.employeeInfoForm.value;
      const hasData = Object.values(formValue).some(value => 
        value !== null && value !== undefined && value !== ''
      );
      
      if (hasData) {
        (this.userSessionService.data as any)['employeeFormData'] = formValue;
      }
    }
  }

  private forceWebComponentUpdate() {
    // Handle all prism-ui input component types
    const componentSelectors = [
      'prism-ui-input-text',
      'prism-ui-input-number',
      'prism-ui-input-currency',
      'prism-ui-input-ssn',
      'prism-ui-input-phone',
      'prism-ui-input-email'
    ];

    componentSelectors.forEach(selector => {
      const inputs = document.querySelectorAll(selector);
      inputs.forEach((input: any) => {
        const formControlName = input.getAttribute('formControlName');
        if (formControlName) {
          const control = this.employeeInfoForm.get(formControlName);
          const controlValue = control?.value;
          if (control && controlValue !== null && controlValue !== undefined && controlValue !== '') {
            // Try to set the value on the web component's shadow DOM
            if (input.shadowRoot) {
              const innerInput = input.shadowRoot.querySelector('input');
              if (innerInput) {
                innerInput.value = controlValue;
                // Dispatch input event to trigger any listeners
                innerInput.dispatchEvent(new Event('input', { bubbles: true }));
              }
            }
            
            // Set the defaultValue property
            input.defaultValue = controlValue;
            
            // Also try setting the value property directly
            if (input.value !== controlValue) {
              input.value = controlValue;
            }
            
            // For SSN and other special components, set the default-value attribute
            if (selector === 'prism-ui-input-ssn' || selector === 'prism-ui-input-phone' || selector === 'prism-ui-input-email') {
              input.setAttribute('default-value', controlValue);
            }
          }
        }
      });
    });
  }

  private disableAutoFill(){

    // Add autocomplete='off' to form elements
    Array.from(document.querySelectorAll('form'))
    .forEach(element => {
      element.setAttribute('autocomplete', 'off');
    });

    Array.from(document.querySelectorAll('input[matInput]'))
    .forEach(element => {
      // Set name equal to id if name attribute doesn't exist
      if(!element.getAttribute("name")){
        element.setAttribute('name', element.getAttribute("id") ?? '');
      }
       // Prepend element names to make them appear unique
      const inputName = element.getAttribute("name") ?? '';
      if(inputName && inputName.indexOf(this.timeInMilliseconds) === -1){
          element.setAttribute('name', this.timeInMilliseconds + "-" + inputName);
      }

      // Set value of autocomplete attribute equal to element name
      // Modern browsers (particularly Chrome) don't honor autocomplete='off'
      element.setAttribute('autocomplete', element.getAttribute("name") ?? '');
    });

    //prismDatepicker
    Array.from(document.querySelectorAll('prism-input-datepicker[prismDatepicker]'))
    .forEach(element => {
      // Set name equal to id if name attribute doesn't exist
      if(!element.getAttribute("name")){
        element.setAttribute('name', element.getAttribute("id") ?? '');
      }
       // Prepend element names to make them appear unique
      const pickerName = element.getAttribute("name") ?? '';
      if(pickerName && pickerName.indexOf(this.timeInMilliseconds) === -1){
          element.setAttribute('name', this.timeInMilliseconds + "-" + pickerName);
      }

      // Set value of autocomplete attribute equal to element name
      // Modern browsers (particularly Chrome) don't honor autocomplete='off'
      element.setAttribute('autocomplete', element.getAttribute("name") ?? '');
    });

    this.employeeBirthDateDayName = this.timeInMilliseconds + "-" + this.employeeBirthDateDayName;
    this.employeeBirthDateMonthName = this.timeInMilliseconds + "-" + this.employeeBirthDateMonthName;
    this.employeeBirthDateYearName = this.timeInMilliseconds + "-" + this.employeeBirthDateYearName;
    this.employeeFirstNameName = this.timeInMilliseconds + "-" + this.employeeFirstNameName;
    this.employeeLastNameName = this.timeInMilliseconds + "-" + this.employeeLastNameName;
    this.employeeHireDateDayName = this.timeInMilliseconds + "-" + this.employeeHireDateDayName;
    this.employeeHireDateMonthName = this.timeInMilliseconds + "-" + this.employeeHireDateMonthName;
    this.employeeHireDateYearName = this.timeInMilliseconds + "-" + this.employeeHireDateYearName;
    this.positionName = this.timeInMilliseconds + "-" + this.positionName;
    this.employeeEarningsName = this.timeInMilliseconds + "-" + this.employeeEarningsName;
    this.employeeSsnName = this.timeInMilliseconds + "-" + this.employeeSsnName;
  }

  //trim beginning and ending whitespace
  firstNameLeaveEvent(event){
    const inputValue = event.target.value;
    this.employeeFirstName.setValue(inputValue.toString().trim());
    this.employeeFirstName.markAsTouched();
  }

  //trim beginning and ending whitespace
  lastNameLeaveEvent(event){
    const inputValue = event.target.value;
    this.employeeLastName.setValue(inputValue.toString().trim());
    this.employeeLastName.markAsTouched();
  }

  occupationLeaveEvent(event){
    const inputValue = (event.target.value).trim();
    this.position.setValue(inputValue);
    this.position.markAsTouched();
    this.employeeInfoForm.updateValueAndValidity();
  }
  
  earningLeaveEvent(event){
    const inputValue = (event.target.value).trim();
    if(inputValue.toString().charAt(inputValue.length -3) == "."){
      let valueWithOutDecimal = inputValue.toString().substring(0,inputValue.length -3);
      this.earnings.setValue(valueWithOutDecimal);
    } else {
      this.earnings.setValue(inputValue);
    }
    
    this.earnings.markAsTouched();
    this.employeeInfoForm.updateValueAndValidity();
  }

  onSubmit(){
    // If in view mode, skip validation and just advance (data already populated and saved)
    if (this.isEmployeeViewMode) {
      this.submitted = true;
      this.val.emit(this.submitted);
      return;
    }
    
    this.formSubmitted = true;
    const spinnerStartTime = new Date().getTime();

    this.appInsightsLoggerService.log('info', 'EmployeeInfoComponent: Spinner Started.' + ' mhsId/caseId: ' + this.userSessionService.data.mhsId, '');

    //always check the ssn value before submitting because we have had issues with the ssn value
    this.formControlUtilitiesService.ssnLeaveEvent(this.employeeInfoForm , 'employeeSsn', this.hasSsnMatch);

    this.touchAllControls();

    this.googleAnalyticsService.sendCustomEvent(this.submitEmployeeInfo);

    FormUtils.markFormAsTouched(this.employeeInfoForm);

    if(!this.employeeInfoForm.valid){
      this.scrollToFirstInvalidControl();

      this.submitted = false;
      this.formSubmitted = false;
    } else {
      if(this.environmentName === 'demonstration'){
        //set the actual ssn to the form control.
        this.trimSSN();
        this.fillInApplicationInfo();
        this.submitted = true;
        this.val.emit(this.submitted);
      } else {
        //set the actual ssn to the form control.
        this.trimSSN();
        this.fillInApplicationInfo();

        if(this.userSessionService.data.mhsId == undefined || this.userSessionService.data.mhsId == null){
          this.userSessionService.data.mhsApplication.formNumber = this.userSessionService.data.formNumber;

          this.savePartialApplication(spinnerStartTime);
        } else {
          this.updateEmployee(spinnerStartTime);
          if(this.userSessionService.data.applicantType?.toLowerCase() === "employee" && this.userSessionService.data.mhsApplication != undefined){
            this.updateApplicant();
          }        
        }
      }
    }

  }

  scrollToFirstInvalidControl() {
    var firstInvalidControlName: string = FormUtils.findFirstInvalidControl(this.employeeInfoForm);
      const firstInvalidControl = document.querySelector(`[formControlName="${firstInvalidControlName}"]`);
      var headerOffset = 170;
      var elementPosition = firstInvalidControl.getBoundingClientRect().top;
      var offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
  }

  async updateEmployee(spinnerStartTime: number){
    let resp = new PartialSaveResp();
    try {
      const result = await this.applicationService.updateEmployeeWithExStreamCloudApplication(this.userSessionService.data.mhsId, this.userSessionService.data.mhsApplication.employee);
      resp = result;
      
      // 201 (Created) and 304 (Not Modified) are both successful responses
      if(resp.httpStatusCode === 201 || resp.httpStatusCode === 304){
        this.appInsightsLoggerService.log('info', 'Employee updated successfully. mhsId/caseId: ' + result.data.mhsId, '');
        this.userSessionService.data.mhsId = result.data.mhsId;
        this.submitted = true;

        //advance to the next step only after partial save is successful
        this.val.emit(this.submitted);
        this.formSubmitted = false;
        this.appInsightsLoggerService.log('info', 'EmployeeInfoComponent: Spinner Stopped.' + ' mhsId/caseId: ' + this.userSessionService.data.mhsId, '');
        const spinnerStopTime = new Date().getTime();
        const spinnerDuration = spinnerStopTime - spinnerStartTime;
        this.appInsightsLoggerService.log('info', 'EmployeeInfoComponent: Spinner Duration: ' + spinnerDuration + 'ms' + ' mhsId/caseId: ' + this.userSessionService.data.mhsId, '');
      } else {
        this.appInsightsLoggerService.log('error', 'ERROR Employee update failed. mhsId/caseId: ' + resp.data.mhsId, '');
        this.submitted = false;
        this.formSubmitted = false;
        this.appInsightsLoggerService.log('info', 'EmployeeInfoComponent: Spinner Stopped.' + ' mhsId/caseId: ' + this.userSessionService.data.mhsId, '');
        const spinnerStopTime = new Date().getTime();
        const spinnerDuration = spinnerStopTime - spinnerStartTime;
        this.appInsightsLoggerService.log('info', 'EmployeeInfoComponent: Spinner Duration: ' + spinnerDuration + 'ms' + ' mhsId/caseId: ' + this.userSessionService.data.mhsId, '');
      }
    } catch (error) {
      let resp = new PartialSaveResp();
      resp.httpStatusCode = error.error.httpStatusCode;
      resp.message = error.message;
      resp.data = error.error.data;
      this.findInvalidControls(resp.data);
      this.appInsightsLoggerService.log('error', 'ERROR saving Partial application.  Component: ' + 'EmployeeInfoComponent' + ' mhsId/caseId: ' + error.data.mhsId, '');
      this.submitted = false;
      this.formSubmitted = false;
      const spinnerStopTime = new Date().getTime();
      const spinnerDuration = spinnerStopTime - spinnerStartTime;
      this.appInsightsLoggerService.log('info', 'EmployeeInfoComponent: Spinner Duration: ' + spinnerDuration + 'ms' + ' mhsId/caseId: ' + this.userSessionService.data.mhsId, '');
    }
  }

  async savePartialApplication(spinnerStartTime:number){
    this.userSessionService.data.mhsApplication.companyId = this.userSessionService.data.companyId;
    let resp = new PartialSaveResp();
    try {
      const result = await this.applicationService.submitPartialCloudApplication(this.userSessionService.data.formNumber, this.userSessionService.data.mhsApplication);
      resp = result;
      
      if(resp.httpStatusCode === 201){
        this.appInsightsLoggerService.log('info', 'Partial application saved successfully.  Component: ' + 'EmployeeInfoComponent' + ' mhsId/caseId: ' + result.data.mhsId, '');
        this.userSessionService.data.mhsId = result.data.mhsId;
        this.submitted = true;

        //advance to the next step only after partial save is successful
        this.val.emit(this.submitted);
        this.formSubmitted = false;
        this.appInsightsLoggerService.log('info', 'EmployeeInfoComponent: Spinner Stopped.' + ' mhsId/caseId: ' + this.userSessionService.data.mhsId, '');
        const spinnerStopTime = new Date().getTime();
        const spinnerDuration = spinnerStopTime - spinnerStartTime;
        this.appInsightsLoggerService.log('info', 'EmployeeInfoComponent: Spinner Duration: ' + spinnerDuration + 'ms' + ' mhsId/caseId: ' + this.userSessionService.data.mhsId, '');
      } else {
        this.appInsightsLoggerService.log('error', 'ERROR saving Partial application.  Component: ' + 'EmployeeInfoComponent' + ' mhsId/caseId: ' + resp.data.mhsId, '');
        this.submitted = false;
        this.formSubmitted = false;
        this.appInsightsLoggerService.log('info', 'EmployeeInfoComponent: Spinner Stopped.' + ' mhsId/caseId: ' + this.userSessionService.data.mhsId, '');
        const spinnerStopTime = new Date().getTime();
        const spinnerDuration = spinnerStopTime - spinnerStartTime;
        this.appInsightsLoggerService.log('info', 'EmployeeInfoComponent: Spinner Duration: ' + spinnerDuration + 'ms' + ' mhsId/caseId: ' + this.userSessionService.data.mhsId, '');
      }
    } catch (error) {
      let resp = new PartialSaveResp();
      resp.httpStatusCode = error.error.httpStatusCode;
      resp.message = error.message;
      resp.data = error.error.data;
      this.findInvalidControls(resp.data);
      this.appInsightsLoggerService.log('error', 'ERROR saving Partial application.  Component: ' + 'EmployeeInfoComponent' + ' mhsId/caseId: ' + error.data.mhsId, '');
      this.submitted = false;
      this.formSubmitted = false;
      this.appInsightsLoggerService.log('info', 'EmployeeInfoComponent: Spinner Stopped.' + ' mhsId/caseId: ' + this.userSessionService.data.mhsId, '');
      const spinnerStopTime = new Date().getTime();
      const spinnerDuration = spinnerStopTime - spinnerStartTime;
      this.appInsightsLoggerService.log('info', 'EmployeeInfoComponent: Spinner Duration: ' + spinnerDuration + 'ms' + ' mhsId/caseId: ' + this.userSessionService.data.mhsId, '');
    }
  }

  findInvalidControls(resp: PartialSaveData){
    for(const key in resp){
      if(key === "employee.firstName"){
        this.employeeInfoForm.get('employeeFirstName').setErrors({ pattern: resp[key] });
      } else if(key === "employee.lastName"){
        this.employeeInfoForm.get('employeeLastName').setErrors({ pattern: resp[key] });
      } else if(key === "employee.earnings"){
        this.employeeInfoForm.get('earnings').setErrors({ pattern: resp[key] });
      } else if(key === "employee.hireDate"){
        this.employeeInfoForm.get('employeeHireDateMonth').setErrors({ pattern: resp[key] });
        this.employeeInfoForm.get('employeeHireDateDay').setErrors({ pattern: resp[key] });
        this.employeeInfoForm.get('employeeHireDateYear').setErrors({ pattern: resp[key] });
      } else if(key === "employee.ssn"){
        this.employeeInfoForm.get('employeeSsn').setErrors({ pattern: resp[key] });
      } else if(key === "employee.position"){
        this.employeeInfoForm.get('position').setErrors({ pattern: resp[key] });
      } else if(key === "employee.birthDate"){
        this.employeeInfoForm.get('employeeBirthDateMonth').setErrors({ pattern: resp[key] });
        this.employeeInfoForm.get('employeeBirthDateDay').setErrors({ pattern: resp[key] });
        this.employeeInfoForm.get('employeeBirthDateYear').setErrors({ pattern: resp[key] });
      } 
    }

    this.scrollToFirstInvalidControl();
  }

  async updateApplicant(){
    let applicant = new ApplicantMhs();
    applicant = this.userSessionService.data.mhsApplication.applicant;
    let employee: EmployeeMhs;
    employee = this.userSessionService.data.mhsApplication.employee;

    applicant.firstName = employee.firstName;
    applicant.lastName = employee.lastName;
    applicant.ssn = employee.ssn;

    this.userSessionService.data.mhsApplication.applicant = applicant;

    let resp = new PartialSaveResp();
    try {
      const result = await this.applicationService.updateApplicantWithExStreamCloudApplication(this.userSessionService.data.mhsId, this.userSessionService.data.mhsApplication.applicant);
      resp = result;
      
      if(resp.httpStatusCode === 201){
        this.appInsightsLoggerService.log('info', 'Applicant updated successfully.  mhsId/caseId: ' + result.data.mhsId, '');
        this.submitted = true;

        this.val.emit(this.submitted);
        this.formSubmitted = false;
      } else {
        this.appInsightsLoggerService.log('error', 'ERROR updating applicant.  mhsId/caseId: ' + resp.data.mhsId, '');
        this.submitted = false;
        this.formSubmitted = false;
      }
    } catch (error) {
      let resp = new PartialSaveResp();
      resp.httpStatusCode = error.error.httpStatusCode;
      resp.message = error.message;
      resp.data = error.error.data;
      this.findInvalidControls(resp.data);
      this.appInsightsLoggerService.log('error', 'ERROR updating applicant.  mhsId/caseId: ' + error.data.mhsId, '');
      this.submitted = false;
      this.formSubmitted = false;
    }
  }

  touchAllControls() {
    this.employeeInfoForm.markAllAsTouched();
    this.employeeBirthDateMonthLeaveEvent();
    this.employeeBirthDateDayLeaveEvent();
    this.employeeBirthDateYearLeaveEvent();
    this.employeeHireDateMonthLeaveEvent();
    this.employeeHireDateDayLeaveEvent();
    this.employeeHireDateYearLeaveEvent();
    this.employeeInfoForm.updateValueAndValidity();
  }

  fillInApplicationInfo(){

    let employee = new EmployeeMhs();

    if(this.userSessionService.data.mhsApplication != undefined){

      employee.firstName = this.employeeFirstName.value;
      employee.lastName = this.employeeLastName.value;
      employee.employer = this.employer.value;
      employee.ssn = this.employeeSsn.value;
      employee.birthDate = this.employeeInfoForm.get('employeeBirthDateYear').value + '-' +
        FormUtils.padWithZero(this.employeeInfoForm.get('employeeBirthDateMonth').value) + '-' +
        FormUtils.padWithZero(this.employeeInfoForm.get('employeeBirthDateDay').value);
      employee.hireDate = this.employeeInfoForm.get('employeeHireDateYear').value + '-' +
        FormUtils.padWithZero(this.employeeInfoForm.get('employeeHireDateMonth').value) + '-' +
        FormUtils.padWithZero(this.employeeInfoForm.get('employeeHireDateDay').value);
      employee.position = this.position.value;
      employee.earnings = this.earnings.value.toString();
      employee.division = this.userSessionService.data.agencyLocationLabel ? this.userSessionService.data.agencyLocationLabel : '';
      employee.signatureDateTime = this.getMonthName(this.today.getMonth() +1) + " " + this.today.getDate() + ", " + this.today.getFullYear();
      this.userSessionService.data.mhsApplication.employee = employee;
    }
  }

  getMonthName(monthNumber: number): string {
    // Create a date with day=1 to avoid rollover issues (e.g., March 31 -> June 31 becomes July 1)
    const date = new Date(2000, monthNumber - 1, 1);
    return date.toLocaleString('en-US', { month: 'long' });
  }

  leaveAnyway(){
    this.userSessionService.clearSession();
    this.userSessionService.data.sessionEnded = true;
    this.authGuardService.cancelLeaveAnyway();
    this.router.navigate(['/instructions']);
  }

  onCancel(){
    this.cancelEmitter.emit();
  }

  /**
   * Edit-mode "Back" button handler. Returns the user to /review
   * without saving any pending changes (matches the coverage page behavior:
   * no confirmation modal because the destination is a review page, not an
   * exit from the application).
   */
  goBack(): void {
    if (this.isSavingEdit) {
      return;
    }
    // Discard any unsaved changes — do not overwrite the session cache.
    this.discardFormDataOnDestroy = true;
    // Mark as a safe navigation so the cancel-modal / leave-anyway flow is
    // bypassed when the deactivate guard fires.
    this.submitted = true;
    this.router.navigate([EEOI_APP_ROUTES.AUTH_ELEC_SIGN]);
  }

  /**
   * Edit-mode "Save" button handler. Validates the form and, if valid,
   * fills the employee model, persists it to the backend, and navigates
   * back to /review with the success toast. On invalid form data
   * the existing field-level errors are surfaced via touchAllControls.
   */
  saveAndGoToReview(): void {
    if (this.isViewOnlyFromReview) {
      this.goBack();
      return;
    }

    // Defend against a double-click while a save is already in flight.
    if (this.isSavingEdit) {
      return;
    }

    this.googleAnalyticsService.sendCustomEvent(this.submitEmployeeInfo);

    // Always normalize the SSN value before validating - the prism input
    // stores the masked value on the control until this is called.
    this.formControlUtilitiesService.ssnLeaveEvent(this.employeeInfoForm, 'employeeSsn', this.hasSsnMatch);
    this.touchAllControls();
    FormUtils.markFormAsTouched(this.employeeInfoForm);

    if (!this.employeeInfoForm.valid) {
      this.scrollToFirstInvalidControl();
      return;
    }

    // Push the form values into the session-level employee model and the
    // in-memory MHS application payload that the final PDF generator reads.
    this.trimSSN();
    this.fillInApplicationInfo();

    this.persistEditedEmployee();
  }

  /**
   * Edit-mode persistence: POST the updated employee to the backend, then
   * (if the applicant IS the employee) also POST the updated applicant so
   * the next final-submit / PDF reflects the edited values. Navigation
   * back to /review happens only after the backend ack.
   */
  private persistEditedEmployee(): void {
    const session = this.userSessionService.data;
    const mhsId = session?.mhsId;
    const employee = session?.mhsApplication?.employee;

    // If we never reached the backend (e.g. user jumped straight to
    // /employee-information?mode=edit in a fresh session) we can't update
    // anything server-side - fall back to local-only navigation so the user
    // isn't stuck.
    if (!mhsId || !employee) {
      this.appInsightsLoggerService.log(
        'warn',
        'Employee edit save: missing mhsId or employee in session, navigating without backend update.',
        ''
      );
      this.navigateBackToReviewAfterSave();
      return;
    }

    this.isSavingEdit = true;
    const startTime = Date.now();

    this.applicationService
      .updateEmployeeWithExStreamCloudApplication(mhsId, employee)
      .then((response: PartialSaveResp) => {
        const code = response?.httpStatusCode;
        // 201 = Created/Updated, 304 = Not Modified - both successful.
        if (code !== 201 && code !== 304) {
          this.appInsightsLoggerService.log(
            'error',
            `Employee edit save: unexpected status ${code} (${Date.now() - startTime}ms). MhsId: ${mhsId}`,
            ''
          );
          this.isSavingEdit = false;
          this.showEmployeeSaveErrorModal();
          return;
        }

        this.appInsightsLoggerService.log(
          'info',
          `Employee edit save: employee updated (status ${code}, ${Date.now() - startTime}ms). MhsId: ${mhsId}`,
          ''
        );

        // When the applicant is the employee, the applicant record stores
        // the same firstName/lastName/ssn/birthDate - keep it in sync so the
        // PDF doesn't show stale values for the applicant section.
        if (session.applicantType?.toLowerCase() === 'employee'
            && session.mhsApplication?.applicant) {
          const applicant: ApplicantMhs = session.mhsApplication.applicant;
          applicant.firstName = employee.firstName;
          applicant.lastName = employee.lastName;
          applicant.ssn = employee.ssn;
          applicant.birthDate = employee.birthDate;
          session.mhsApplication.applicant = applicant;

          this.applicationService
            .updateApplicantWithExStreamCloudApplication(mhsId, applicant)
            .then((applicantResp: PartialSaveResp) => {
              const aCode = applicantResp?.httpStatusCode;
              if (aCode === 201 || aCode === 304) {
                this.appInsightsLoggerService.log(
                  'info',
                  `Employee edit save: applicant kept in sync (status ${aCode}). MhsId: ${mhsId}`,
                  ''
                );
              } else {
                this.appInsightsLoggerService.log(
                  'error',
                  `Employee edit save: applicant sync returned ${aCode}. MhsId: ${mhsId}`,
                  ''
                );
              }
              this.isSavingEdit = false;
              this.navigateBackToReviewAfterSave();
            })
            .catch((err: any) => {
              // Treat the applicant sync as best-effort: the primary
              // employee write already succeeded so we still navigate back.
              this.appInsightsLoggerService.log(
                'error',
                `Employee edit save: applicant sync failed. MhsId: ${mhsId}. Error: ${JSON.stringify(err)}`,
                ''
              );
              this.isSavingEdit = false;
              this.navigateBackToReviewAfterSave();
            });
        } else {
          this.isSavingEdit = false;
          this.navigateBackToReviewAfterSave();
        }
      })
      .catch((error: any) => {
        this.appInsightsLoggerService.log(
          'error',
          `Employee edit save: update failed (${Date.now() - startTime}ms). MhsId: ${mhsId}. Error: ${JSON.stringify(error)}`,
          ''
        );
        this.isSavingEdit = false;
        this.showEmployeeSaveErrorModal();
      });
  }

  /**
   * Common post-save navigation: flip the success-toast flag and route back
   * to /review. Mirrors the coverage page's helper of the same name
   * so the user sees the same success banner regardless of which section
   * they edited.
   */
  private navigateBackToReviewAfterSave(): void {
    this.userSessionService.data['showCoverageSavedToast'] = true;
    sessionStorage.setItem('showCoverageSavedToast', 'true');
    this.mhsService.allowReviewNavigationAfterSaveOnce = true;

    const destinationRoute = EEOI_APP_ROUTES.AUTH_ELEC_SIGN;
    this.submitted = true;
    this.router.navigate([destinationRoute], { state: { showSuccess: true } });
    this.appInsightsApiService.startNavigationEvent(destinationRoute.toString());
  }

  /**
   * Surfaces the existing generic error modal when the backend rejected
   * the save, leaving the user on the page so they can retry by clicking
   * Save again.
   */
  private showEmployeeSaveErrorModal(): void {
    this.modalService.showModal('prism-ui-modal-error-modal');
  }

  // Format SSN to show only last 4 digits with masked first 5 digits
  getMaskedSsn(ssn: string): string {
    if (!ssn) return '';
    // Remove any non-digit characters
    const digits = ssn.replace(/\D/g, '');
    if (digits.length < 4) return ssn; // Return as-is if less than 4 digits
    
    // Get last 4 digits
    const lastFour = digits.slice(-4);
    // Return masked format: ***-**-1234
    return `*** ** ${lastFour}`;
  }

  // Format date for display (MM/DD/YYYY)
  getFormattedDate(month: string, day: string, year: string): string {
    if (!month || !day || !year) return '';
    return `${month.padStart(2, '0')}/${day.padStart(2, '0')}/${year}`;
  }

  canDeactivate(){
    if (!this.submitted && !this.userSessionService.data.sessionEnded){
      this.modalService.showModal('prism-ui-modal-cancel-modal'); // Use the service to show the modal
      return this.userSessionService.data.sessionEnded;
    } else {
      return of(true);
    }
  }

  employeeBirthDateMonthLeaveEvent() {
    this.employeeInfoForm.get('employeeBirthDateMonth').markAsTouched();
    this.employeeInfoForm.get('employeeBirthDateMonth').updateValueAndValidity();

    //validate month
    if(this.formControlUtilitiesService.checkIfControlIsTouchedAndNotNullOrEmpty(this.employeeInfoForm, 'employeeBirthDateMonth')){
      if(this.formControlUtilitiesService.checkIfMonthControlIsBetweenOneAndTwelve(this.employeeInfoForm, 'employeeBirthDateMonth')){
        this.employeeInfoForm.get('employeeBirthDateMonth').setErrors(null);
      } else {
        this.employeeInfoForm.get('employeeBirthDateMonth').setErrors({ dateError: true });
        return;
      }
    } else {
      this.employeeInfoForm.get('employeeBirthDateMonth').setErrors({ dateError: true });
      return;
    }

    this.isDateofBirthFutureDated();
  }

  employeeHireDateMonthLeaveEvent() {
    this.employeeInfoForm.get('employeeHireDateMonth').markAsTouched();
    this.employeeInfoForm.get('employeeHireDateMonth').updateValueAndValidity();
    //validate month
    if(this.formControlUtilitiesService.checkIfControlIsTouchedAndNotNullOrEmpty(this.employeeInfoForm, 'employeeHireDateMonth')){
      if(this.formControlUtilitiesService.checkIfMonthControlIsBetweenOneAndTwelve(this.employeeInfoForm, 'employeeHireDateMonth')) {
        this.employeeInfoForm.get('employeeHireDateMonth').setErrors(null);
      } else {
        this.employeeInfoForm.get('employeeHireDateMonth').setErrors({ dateError: true });
        return;
      }
    } else {
      this.employeeInfoForm.get('employeeHireDateMonth').setErrors({ dateError: true });
      return;
    }   

    this.dateOfHireCanBeFutureDated();
    this.checkMinimumAgeRequirementForHireDate();
  }

  employeeBirthDateDayLeaveEvent() {
    this.employeeInfoForm.get('employeeBirthDateDay').markAsTouched();
    this.employeeInfoForm.get('employeeBirthDateDay').updateValueAndValidity();

    //validate day
    if(this.formControlUtilitiesService.checkIfControlIsTouchedAndNotNullOrEmpty(this.employeeInfoForm, 'employeeBirthDateDay')){
      if(this.formControlUtilitiesService.checkIfDayControlIsBetweenOneAndThirtyOne(this.employeeInfoForm, 'employeeBirthDateDay')) { 
        this.employeeInfoForm.get('employeeBirthDateDay').setErrors(null);
      } else {
        this.employeeInfoForm.get('employeeBirthDateDay').setErrors({ dateError: true });
        return;
      }
    } else {
      this.employeeInfoForm.get('employeeBirthDateDay').setErrors({ dateError: true });
      return;
    }

    this.isDateofBirthFutureDated();
  }

  employeeHireDateDayLeaveEvent() {
    this.employeeInfoForm.get('employeeHireDateDay').markAsTouched();
    this.employeeInfoForm.get('employeeHireDateDay').updateValueAndValidity();      

    //validate day  
    if(this.formControlUtilitiesService.checkIfControlIsTouchedAndNotNullOrEmpty(this.employeeInfoForm, 'employeeHireDateDay')){
      if(this.formControlUtilitiesService.checkIfDayControlIsBetweenOneAndThirtyOne(this.employeeInfoForm, 'employeeHireDateDay')) {
        this.employeeInfoForm.get('employeeHireDateDay').setErrors(null);
      } else {
        this.employeeInfoForm.get('employeeHireDateDay').setErrors({ dateError: true });
        return;
      }
    } else {
      this.employeeInfoForm.get('employeeHireDateDay').setErrors({ dateError: true });
      return;
    }

    this.dateOfHireCanBeFutureDated();
    this.checkMinimumAgeRequirementForHireDate();
  }

  employeeBirthDateYearLeaveEvent() {
    this.employeeInfoForm.get('employeeBirthDateYear').markAsTouched();
    this.employeeInfoForm.get('employeeBirthDateYear').updateValueAndValidity();

    //validate year
    if(this.formControlUtilitiesService.checkIfControlIsTouchedAndNotNullOrEmpty(this.employeeInfoForm, 'employeeBirthDateYear')) {
      if(this.formControlUtilitiesService.checkIfYearControlIsValid(this.employeeInfoForm, 'employeeBirthDateYear', false)) {
        this.employeeInfoForm.get('employeeBirthDateYear').setErrors(null);
      } else {
        this.employeeInfoForm.get('employeeBirthDateYear').setErrors({ dateError: true });
        return;
      }
    } else {
      this.employeeInfoForm.get('employeeBirthDateYear').setErrors({ dateError: true });
      return;
    }

    this.isDateofBirthFutureDated();
  }

  employeeHireDateYearLeaveEvent() {
    this.employeeInfoForm.get('employeeHireDateYear').markAsTouched();
    this.employeeInfoForm.get('employeeHireDateYear').updateValueAndValidity(); 

    //validate year 
    if(this.formControlUtilitiesService.checkIfControlIsTouchedAndNotNullOrEmpty(this.employeeInfoForm, 'employeeHireDateYear')) {
      if(this.formControlUtilitiesService.checkIfYearControlIsValid(this.employeeInfoForm, 'employeeHireDateYear', true)) {
        this.employeeInfoForm.get('employeeHireDateYear').setErrors(null);
      } else {
        this.employeeInfoForm.get('employeeHireDateYear').setErrors({ dateError: true });
        return;
      }
    } else {
      this.employeeInfoForm.get('employeeHireDateYear').setErrors({ dateError: true });
      return;
    }

    this.dateOfHireCanBeFutureDated();
    this.checkMinimumAgeRequirementForHireDate();
  }

  isEmployeeBirthDateValid() {
    return this.employeeInfoForm.get('employeeBirthDateMonth').valid &&
           this.employeeInfoForm.get('employeeBirthDateDay').valid &&
           this.employeeInfoForm.get('employeeBirthDateYear').valid;
  }

  isEmployeeHireDateValid() {
    return this.employeeInfoForm.get('employeeHireDateMonth').valid &&
           this.employeeInfoForm.get('employeeHireDateDay').valid &&
           this.employeeInfoForm.get('employeeHireDateYear').valid;
  }

  //can't future date dob
  isDateofBirthFutureDated(){
    let today = new Date();
    let tempDate = new Date(this.employeeInfoForm.get('employeeBirthDateYear').value, this.employeeInfoForm.get('employeeBirthDateMonth').value -1, this.employeeInfoForm.get('employeeBirthDateDay').value);
    if(tempDate > today){
      this.employeeInfoForm.get('employeeBirthDateMonth').setErrors({ dateError: true });
      this.employeeInfoForm.get('employeeBirthDateDay').setErrors({ dateError: true });
      this.employeeInfoForm.get('employeeBirthDateYear').setErrors({ dateError: true });
    } else {
      if(this.formControlUtilitiesService.checkIfControlIsTouchedAndNotNullOrEmpty(this.employeeInfoForm, 'employeeBirthDateYear') &&
         this.formControlUtilitiesService.checkIfControlIsTouchedAndNotNullOrEmpty(this.employeeInfoForm, 'employeeBirthDateMonth') &&
         this.formControlUtilitiesService.checkIfControlIsTouchedAndNotNullOrEmpty(this.employeeInfoForm, 'employeeBirthDateDay') &&
         this.formControlUtilitiesService.checkIfYearControlIsValid(this.employeeInfoForm, 'employeeBirthDateYear', false) &&
         this.formControlUtilitiesService.checkIfDayControlIsBetweenOneAndThirtyOne(this.employeeInfoForm, 'employeeBirthDateDay') &&
         this.formControlUtilitiesService.checkIfMonthControlIsBetweenOneAndTwelve(this.employeeInfoForm, 'employeeBirthDateMonth')){

        this.employeeInfoForm.get('employeeBirthDateMonth').setErrors(null);
        this.employeeInfoForm.get('employeeBirthDateDay').setErrors(null);
        this.employeeInfoForm.get('employeeBirthDateYear').setErrors(null);
      }
    }
  }

  //DATE of hire validation
  dateOfHireCanBeFutureDated(){
    let today = new Date();
    let endHireDate = new Date(today.setMonth(today.getMonth()+ GlobalConstants.ADD_MONTH_IN_DATE));
    let tempDate = new Date(this.employeeInfoForm.get('employeeHireDateYear').value, this.employeeInfoForm.get('employeeHireDateMonth').value -1, this.employeeInfoForm.get('employeeHireDateDay').value);
    if(tempDate > endHireDate ){
      this.employeeInfoForm.get('employeeHireDateMonth').setErrors({ dateError: true });
      this.employeeInfoForm.get('employeeHireDateDay').setErrors({ dateError: true });
      this.employeeInfoForm.get('employeeHireDateYear').setErrors({ dateError: true });
    } else {
      if(this.formControlUtilitiesService.checkIfControlIsTouchedAndNotNullOrEmpty(this.employeeInfoForm, 'employeeHireDateYear') &&
         this.formControlUtilitiesService.checkIfControlIsTouchedAndNotNullOrEmpty(this.employeeInfoForm, 'employeeHireDateMonth') &&
         this.formControlUtilitiesService.checkIfControlIsTouchedAndNotNullOrEmpty(this.employeeInfoForm, 'employeeHireDateDay') &&
         this.formControlUtilitiesService.checkIfYearControlIsValid(this.employeeInfoForm, 'employeeHireDateYear', true) &&
         this.formControlUtilitiesService.checkIfDayControlIsBetweenOneAndThirtyOne(this.employeeInfoForm, 'employeeHireDateDay') &&
         this.formControlUtilitiesService.checkIfMonthControlIsBetweenOneAndTwelve(this.employeeInfoForm, 'employeeHireDateMonth')){

        this.employeeInfoForm.get('employeeHireDateMonth').setErrors(null);
        this.employeeInfoForm.get('employeeHireDateDay').setErrors(null);
        this.employeeInfoForm.get('employeeHireDateYear').setErrors(null);
      }
    }
  }

  //You must be at least 15 years old to work (any state)
  checkMinimumAgeRequirementForHireDate(){
    if(this.formControlUtilitiesService.checkIfControlIsTouchedAndNotNullOrEmpty(this.employeeInfoForm, 'employeeBirthDateYear') &&
       this.formControlUtilitiesService.checkIfControlIsTouchedAndNotNullOrEmpty(this.employeeInfoForm, 'employeeBirthDateMonth') &&
       this.formControlUtilitiesService.checkIfControlIsTouchedAndNotNullOrEmpty(this.employeeInfoForm, 'employeeBirthDateDay') &&
       this.formControlUtilitiesService.checkIfControlIsTouchedAndNotNullOrEmpty(this.employeeInfoForm, 'employeeHireDateYear') &&
       this.formControlUtilitiesService.checkIfControlIsTouchedAndNotNullOrEmpty(this.employeeInfoForm, 'employeeHireDateDay') &&
       this.formControlUtilitiesService.checkIfControlIsTouchedAndNotNullOrEmpty(this.employeeInfoForm, 'employeeHireDateMonth')){

      let birthYearPlusMinAge: number = Number(this.employeeInfoForm.get('employeeBirthDateYear').value) + Number(GlobalConstants.MIN_AGE);
      let birthdatePlusMinAge = new Date(birthYearPlusMinAge, this.employeeInfoForm.get('employeeBirthDateMonth').value -1, this.employeeInfoForm.get('employeeBirthDateDay').value);
      let formHireDate = new Date(this.employeeInfoForm.get('employeeHireDateYear').value, this.employeeInfoForm.get('employeeHireDateMonth').value -1, this.employeeInfoForm.get('employeeHireDateDay').value);
      if(formHireDate < birthdatePlusMinAge){
        this.employeeInfoForm.get('employeeHireDateMonth').setErrors({ dateError: true });
        this.employeeInfoForm.get('employeeHireDateDay').setErrors({ dateError: true });
        this.employeeInfoForm.get('employeeHireDateYear').setErrors({ dateError: true });
      }
    }
  }

  ssnMatchesApplicantValidator() {
      return (control: AbstractControl): { [key: string]: boolean } | null => {
        let applicant = new ApplicantMhs();
        if(this.userSessionService.data.mhsApplication.applicant != undefined){
          applicant = this.userSessionService.data.mhsApplication.applicant;
          let employeeSsn = this.employeeInfoForm.get("actualSsn").value;
          if(applicant.ssn === employeeSsn){
            this.hasSsnMatch = true;
            return {ssnMatchesEmployee:true}
          }
        }
        return null;
      };
    }
}